import { ExternalLink, Sparkles } from 'lucide-react';
import { ProductListing } from '../types';

interface AdCardProps {
  product: ProductListing;
}

const tierColors = {
  PLATINUM_FEATURED: 'bg-gradient-to-r from-purple-600 to-purple-700',
  GOLD_FEATURED: 'bg-gradient-to-r from-yellow-500 to-yellow-600',
  SILVER_SPONSORED: 'bg-gradient-to-r from-gray-400 to-gray-500',
  BRONZE_INLINE: 'bg-gradient-to-r from-orange-500 to-orange-600',
};

const tierLabels = {
  PLATINUM_FEATURED: 'Platinum Featured',
  GOLD_FEATURED: 'Gold Featured',
  SILVER_SPONSORED: 'Silver Sponsored',
  BRONZE_INLINE: 'Bronze Inline',
};

export function AdCard({ product }: AdCardProps) {
  const images = product.images || [];
  const image = images[0];
  const tier = product.ad_tier || 'BRONZE_INLINE';
  const tierColor = tierColors[tier as keyof typeof tierColors] || tierColors.BRONZE_INLINE;
  const tierLabel = tierLabels[tier as keyof typeof tierLabels] || 'Sponsored';

  return (
    <div className="card card-hover overflow-hidden group border-2 border-accent-200 dark:border-accent-800 relative">
      {/* Ad Badge */}
      <div className={`absolute top-2 left-2 z-10 px-2 sm:px-3 py-1 rounded-lg text-[10px] sm:text-xs font-bold text-white shadow-lg ${tierColor} flex items-center gap-1 sm:gap-1.5`}>
        <Sparkles size={10} className="sm:w-3 sm:h-3" />
        <span>{tierLabel}</span>
      </div>

      {/* Image Section */}
      <div className="relative overflow-hidden bg-neutral-100 dark:bg-neutral-800 aspect-square">
        {image ? (
          <img
            src={image}
            alt={product.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-300 dark:text-neutral-600 bg-gradient-to-br from-accent-50 to-accent-100 dark:from-accent-900/20 dark:to-accent-800/20">
            <Sparkles size={40} className="sm:w-12 sm:h-12 text-accent-400" />
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0" />
      </div>

      {/* Content Section */}
      <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
        {/* Title */}
        <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 line-clamp-2 leading-snug group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors text-sm sm:text-base">
          {product.title}
        </h3>

        {/* Description */}
        {product.description && (
          <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2">
            {product.description}
          </p>
        )}

        {/* Advertiser Info */}
        {product.advertiser_name && (
          <div className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400">
            <span className="font-medium">By {product.advertiser_name}</span>
          </div>
        )}

        {/* CTA Button */}
        <a
          href={product.url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full btn-primary py-2 sm:py-2.5 text-xs sm:text-sm rounded-lg flex items-center justify-center gap-1.5 sm:gap-2"
        >
          <ExternalLink size={14} className="sm:w-4 sm:h-4" />
          Learn More
        </a>
      </div>
    </div>
  );
}


